<script setup>
import { ref, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'dashboard/components-next/dropdown-menu/base/DropdownItem.vue';
import { provideDropdownContext } from 'dashboard/components-next/dropdown-menu/base/provider.js';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  menuOption: {
    type: Array,
    default: () => [],
  },
  showMenu: {
    type: Boolean,
    default: false,
  },
  placeholderI18nKey: {
    type: String,
    default: '',
  },
  emptyStateMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['toggleDropdown', 'addFilter', 'closeDropdown']);

const hoveredItemId = ref(null);
const searchTerm = ref('');

const showSubMenu = id => {
  hoveredItemId.value = id;
};

const hideSubMenu = () => {
  hoveredItemId.value = null;
  searchTerm.value = '';
};

const isHovered = id => hoveredItemId.value === id;

const toggleDropdown = () => emit('toggleDropdown');

const addFilter = item => {
  emit('addFilter', item);
  hideSubMenu();
};

const closeDropdown = () => {
  hideSubMenu();
  emit('closeDropdown');
};

const filterListItems = options => {
  if (!searchTerm.value) return options;
  return options.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
};

const dropdownIsOpen = ref(props.showMenu);
watch(
  () => props.showMenu,
  val => {
    dropdownIsOpen.value = val;
  }
);

provideDropdownContext({
  isOpen: dropdownIsOpen,
  toggle: toggleDropdown,
  closeMenu: closeDropdown,
});
</script>

<template>
  <div class="relative">
    <NextButton
      ghost
      slate
      sm
      no-animation
      icon="i-lucide-filter"
      :label="name"
      @click="toggleDropdown"
    />
    <DropdownBody
      v-if="showMenu"
      v-on-clickaway="closeDropdown"
      class="left-0 md:left-auto md:right-0 top-10 w-48 z-50"
    >
      <div
        v-if="!menuOption.length"
        class="text-sm text-n-slate-11 px-2 py-1.5"
      >
        {{ emptyStateMessage }}
      </div>
      <DropdownItem
        v-for="item in menuOption"
        :key="item.id"
        :label="item.name"
        class="relative"
        @mouseenter="showSubMenu(item.id)"
        @mouseleave="hideSubMenu"
        @focus="showSubMenu(item.id)"
      >
        <!-- Submenu with search -->
        <DropdownBody
          v-if="item.options && isHovered(item.id)"
          strong
          class="flex flex-col w-[216px] max-h-[300px] overflow-y-auto top-0 left-44 z-50"
        >
          <!-- Search input inside sub-dropdown -->
          <div class="px-2 pb-2 pt-1">
            <input
              v-model="searchTerm"
              type="search"
              :placeholder="
                $t(`${placeholderI18nKey}.${item.type.toUpperCase()}`)
              "
              class="reset-base w-full h-8 py-2 px-2 text-sm focus:outline-none border border-n-weak rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
              @click.stop
            />
          </div>
          <!-- Submenu items -->
          <div class="max-h-[200px] overflow-y-auto flex flex-col gap-0.5">
            <button
              v-for="opt in filterListItems(item.options)"
              :key="opt.id"
              type="button"
              class="inline-flex items-center justify-start w-full h-8 px-2 py-1.5 text-sm rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 text-n-slate-12 text-left"
              @click.stop="addFilter(opt)"
            >
              {{ opt.name }}
            </button>
          </div>
        </DropdownBody>
      </DropdownItem>
    </DropdownBody>
  </div>
</template>
